// ===================================================================
// HMGP — Analytics Dashboard
// Visual data: phase pipeline, type distribution, BCA stats,
// financial summary, subapplication funnel
// ===================================================================

import { useState, useEffect, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Progress, SimpleGrid, Center, Loader, Alert, Tooltip,
} from '../../../components/gab/GabComponents';
import { IconChartBar, IconAlertCircle } from '@tabler/icons-react';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useActivePADisaster as useActiveDisaster } from '../../../hooks/useActiveDisaster';
import {
  HMGP_PROJECT_TYPE_LABELS,
  HMGP_PROJECT_TYPE_COLORS,
  HMGP_PHASE_LABELS,
  HMGP_SUBAPP_STATUS_LABELS,
  type HMGPProjectType,
  type HMGPPhase,
  type HMGPSubappStatus,
} from '../../../types/pagrants';

function num(v: string | number): number {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? 0 : n;
}

function fmtCurrency(v: number): string {
  if (v === 0) return '$0';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

const PHASE_ORDER: HMGPPhase[] = [
  'subapplication', 'state_review', 'fema_review', 'selected', 'awarded', 'implementation', 'closeout',
];

const SUBAPP_FUNNEL_ORDER: HMGPSubappStatus[] = [
  'draft', 'submitted', 'under_review', 'prioritized', 'forwarded_to_fema',
];

// Mantine Progress color names mapped from HMGP_PHASE_COLORS
const PHASE_MANTINE_COLORS: Record<HMGPPhase, string> = {
  subapplication: 'blue',
  state_review: 'yellow',
  fema_review: 'orange',
  selected: 'teal',
  awarded: 'green',
  implementation: 'violet',
  closeout: 'gray',
};

export default function HMGPAnalytics() {
  const { projects, subapplications, fetchProjects, fetchSubapplications } = useHMGPStore();
  const { fetchOrganizations } = usePAGrantsStore();
  const activeDisaster = useActiveDisaster();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchProjects(), fetchSubapplications(), fetchOrganizations()]).finally(() => setLoading(false));
  }, []);

  const filteredProjects = useMemo(() => {
    if (!activeDisaster) return [];
    return projects.filter(p => String(p.disasterId) === String(activeDisaster.Rid));
  }, [projects, activeDisaster]);

  const filteredSubapps = useMemo(() => {
    if (!activeDisaster) return [];
    return subapplications.filter(s => String(s.disasterId) === String(activeDisaster.Rid));
  }, [subapplications, activeDisaster]);

  // ── Computed analytics ────────────────────────────────────────

  // Phase pipeline
  const phaseCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    PHASE_ORDER.forEach(p => { counts[p] = 0; });
    filteredProjects.forEach(p => { counts[p.phase] = (counts[p.phase] || 0) + 1; });
    return counts;
  }, [filteredProjects]);

  const totalProjects = filteredProjects.length;

  // Type distribution
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filteredProjects.forEach(p => { counts[p.projectType] = (counts[p.projectType] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [filteredProjects]);

  const maxTypeCount = typeCounts.length > 0 ? typeCounts[0][1] : 1;

  // BCA stats
  const bcaStats = useMemo(() => {
    const withBCA = filteredProjects.filter(p => num(p.bcaRatio) > 0);
    const avgBCR = withBCA.length > 0
      ? withBCA.reduce((s, p) => s + num(p.bcaRatio), 0) / withBCA.length
      : 0;
    const costEffective = withBCA.filter(p => num(p.bcaRatio) >= 1.0).length;
    const missing = filteredProjects.length - withBCA.length;
    return { avgBCR, costEffective, withBCA: withBCA.length, missing, total: filteredProjects.length };
  }, [filteredProjects]);

  // Financial summary
  const financial = useMemo(() => {
    const totalEstimated = filteredProjects.reduce((s, p) => s + num(p.estimatedCost), 0);
    const totalAwarded = filteredProjects.reduce((s, p) => s + num(p.awardAmount), 0);
    const totalObligated = filteredProjects.reduce((s, p) => s + num(p.obligatedAmount), 0);
    const totalDrawn = filteredProjects.reduce((s, p) => s + num(p.drawdownAmount), 0);
    const utilRate = totalAwarded > 0 ? (totalDrawn / totalAwarded) * 100 : 0;
    return { totalEstimated, totalAwarded, totalObligated, totalDrawn, utilRate };
  }, [filteredProjects]);

  // Subapplication funnel
  const funnelCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    SUBAPP_FUNNEL_ORDER.forEach(s => { counts[s] = 0; });
    filteredSubapps.forEach(s => { counts[s.status] = (counts[s.status] || 0) + 1; });
    return counts;
  }, [filteredSubapps]);

  const maxFunnelCount = Math.max(1, ...SUBAPP_FUNNEL_ORDER.map(s => funnelCounts[s] || 0));

  if (loading) return <Center h={200}><Loader /></Center>;

  if (!activeDisaster) {
    return (
      <Stack p="md">
        <Title order={3}>HMGP Analytics</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="No Active Disaster">
          Select an active incident in the top bar to view HMGP analytics.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack p="md" gap="md">
      <Group justify="space-between">
        <div>
          <Title order={3}>HMGP Analytics</Title>
          <Text size="sm" c="dimmed">
            {filteredProjects.length} projects, {filteredSubapps.length} subapplications
          </Text>
        </div>
        <Badge color="green" variant="light" leftSection={<IconChartBar size={12} />}>
          {activeDisaster.disasterName}
        </Badge>
      </Group>

      {/* 1. Phase Pipeline */}
      <Paper p="md" withBorder>
        <Text size="sm" fw={600} mb="sm">Phase Pipeline</Text>
        {totalProjects === 0 ? (
          <Text c="dimmed" size="sm">No projects to display.</Text>
        ) : (
          <Stack gap="xs">
            <Progress.Root size={28}>
              {PHASE_ORDER.map(phase => {
                const count = phaseCounts[phase] || 0;
                if (count === 0) return null;
                const pct = (count / totalProjects) * 100;
                return (
                  <Tooltip key={phase} label={`${HMGP_PHASE_LABELS[phase]}: ${count} (${pct.toFixed(0)}%)`}>
                    <Progress.Section value={pct} color={PHASE_MANTINE_COLORS[phase]} />
                  </Tooltip>
                );
              })}
            </Progress.Root>
            <Group gap="md" wrap="wrap">
              {PHASE_ORDER.map(phase => {
                const count = phaseCounts[phase] || 0;
                if (count === 0) return null;
                return (
                  <Group key={phase} gap={4}>
                    <Badge size="xs" color={PHASE_MANTINE_COLORS[phase]} variant="filled">{count}</Badge>
                    <Text size="xs">{HMGP_PHASE_LABELS[phase]}</Text>
                  </Group>
                );
              })}
            </Group>
          </Stack>
        )}
      </Paper>

      {/* 2. Project Type Distribution */}
      <Paper p="md" withBorder>
        <Text size="sm" fw={600} mb="sm">Project Type Distribution</Text>
        {typeCounts.length === 0 ? (
          <Text c="dimmed" size="sm">No projects to display.</Text>
        ) : (
          <Stack gap={6}>
            {typeCounts.map(([type, count]) => {
              const color = HMGP_PROJECT_TYPE_COLORS[type as HMGPProjectType] || '#059669';
              const pct = (count / maxTypeCount) * 100;
              return (
                <Group key={type} gap="sm" wrap="nowrap">
                  <Text size="xs" w={160} style={{ flexShrink: 0 }}>
                    {HMGP_PROJECT_TYPE_LABELS[type as HMGPProjectType] || type}
                  </Text>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        width: `${pct}%`, minWidth: 24, height: 20, borderRadius: 4,
                        background: color, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', transition: 'width 0.3s',
                      }}
                    >
                      <Text size="xs" c="white" fw={600}>{count}</Text>
                    </div>
                  </div>
                </Group>
              );
            })}
          </Stack>
        )}
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* 3. BCA Stats */}
        <Paper p="md" withBorder>
          <Text size="sm" fw={600} mb="sm">Benefit-Cost Analysis</Text>
          <SimpleGrid cols={2}>
            <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
              <Text size="xs" c="dimmed">Avg BCR</Text>
              <Text size="lg" fw={700}>{bcaStats.avgBCR > 0 ? bcaStats.avgBCR.toFixed(2) : '-'}</Text>
            </Paper>
            <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #0284C7' }}>
              <Text size="xs" c="dimmed">BCR &ge; 1.0</Text>
              <Text size="lg" fw={700}>
                {bcaStats.withBCA > 0
                  ? `${bcaStats.costEffective}/${bcaStats.withBCA} (${((bcaStats.costEffective / bcaStats.withBCA) * 100).toFixed(0)}%)`
                  : '-'}
              </Text>
            </Paper>
            <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #D97706' }}>
              <Text size="xs" c="dimmed">Missing BCA</Text>
              <Text size="lg" fw={700} c={bcaStats.missing > 0 ? 'orange' : 'dimmed'}>{bcaStats.missing}</Text>
            </Paper>
            <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #7C3AED' }}>
              <Text size="xs" c="dimmed">Total Analyzed</Text>
              <Text size="lg" fw={700}>{bcaStats.withBCA} / {bcaStats.total}</Text>
            </Paper>
          </SimpleGrid>
        </Paper>

        {/* 4. Financial Summary */}
        <Paper p="md" withBorder>
          <Text size="sm" fw={600} mb="sm">Financial Summary</Text>
          <SimpleGrid cols={2}>
            <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
              <Text size="xs" c="dimmed">Total Estimated</Text>
              <Text size="lg" fw={700}>{fmtCurrency(financial.totalEstimated)}</Text>
            </Paper>
            <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #0284C7' }}>
              <Text size="xs" c="dimmed">Total Awarded</Text>
              <Text size="lg" fw={700}>{fmtCurrency(financial.totalAwarded)}</Text>
            </Paper>
            <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #7C3AED' }}>
              <Text size="xs" c="dimmed">Total Drawn</Text>
              <Text size="lg" fw={700}>{fmtCurrency(financial.totalDrawn)}</Text>
            </Paper>
            <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #D97706' }}>
              <Text size="xs" c="dimmed">Utilization Rate</Text>
              <Text size="lg" fw={700}>{financial.utilRate.toFixed(1)}%</Text>
            </Paper>
          </SimpleGrid>
        </Paper>
      </SimpleGrid>

      {/* 5. Subapplication Funnel */}
      <Paper p="md" withBorder>
        <Text size="sm" fw={600} mb="sm">Subapplication Funnel</Text>
        {filteredSubapps.length === 0 ? (
          <Text c="dimmed" size="sm">No subapplications to display.</Text>
        ) : (
          <Stack gap={6}>
            {SUBAPP_FUNNEL_ORDER.map((status, idx) => {
              const count = funnelCounts[status] || 0;
              const pct = (count / maxFunnelCount) * 100;
              // Gradient from blue to green as subapps progress
              const colors = ['#3B82F6', '#6366F1', '#8B5CF6', '#059669', '#10B981'];
              const color = colors[idx] || '#6B7280';
              return (
                <Group key={status} gap="sm" wrap="nowrap">
                  <Text size="xs" w={140} style={{ flexShrink: 0 }}>
                    {HMGP_SUBAPP_STATUS_LABELS[status] || status}
                  </Text>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        width: `${Math.max(pct, 3)}%`, height: 22, borderRadius: 4,
                        background: color, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', transition: 'width 0.3s',
                      }}
                    >
                      <Text size="xs" c="white" fw={600}>{count}</Text>
                    </div>
                  </div>
                </Group>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
